import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { useState, useEffect, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TimePicker } from 'antd';
import styles from './Regularization.module.scss';
import dayjs from 'dayjs';
import { textVariantsEnum, fontWeightEnum, Text } from '@/components/Text';
import {
  getProjectDropdownOptions,
  getTaskDropdownOptions,
  getManagerDropdownOptions,
  getDefaultTimezone,
  calculateDuration,
  validateForm,
  getHoursMinutes,
  getInitialFormData,
  getTeamDropdownOptions,
  check24HoursLimit,
  canUseRegularizationDateSelection,
  isRegularizationDateSelectableInCalendar,
  buildRegularizationFormDateChange,
  managersFromMemberDetails,
} from './Regularization.helper';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button, buttonVariants } from '@/components/Button';
import { regularizationActionTypes } from '@/store/Regularization/Regularization.actionTypes';
import { useTimezones } from '@/hooks/useTimezones';
import { useUserTeamsForCompany } from '@/hooks/useUserTeamsForCompany';
import { useTeamMemberDetailsForSelect } from '@hooks/useTeamMemberDetailsForSelect';

const timePickerPopupProps = {
  getPopupContainer: () => document.body,
  popupStyle: { zIndex: 1200 },
};

const parseDateValue = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date)
    return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object') {
    const seconds =
      typeof value._seconds === 'number'
        ? value._seconds
        : typeof value.seconds === 'number'
          ? value.seconds
          : null;
    if (seconds != null) {
      const nanos =
        typeof value._nanoseconds === 'number'
          ? value._nanoseconds
          : typeof value.nanoseconds === 'number'
            ? value.nanoseconds
            : 0;
      const millis = seconds * 1000 + Math.floor(nanos / 1000000);
      const parsed = new Date(millis);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
  }
  if (typeof value === 'string') {
    const isoDateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (isoDateMatch) {
      const [, year, month, day] = isoDateMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const getRowStartDate = (rowData) => {
  const direct = parseDateValue(rowData?.startTime);
  if (direct) return direct;

  if (
    typeof rowData?.startTime === 'string' &&
    rowData?.startHours != null &&
    rowData?.startMinutes != null
  ) {
    const base = new Date(rowData.startTime);
    if (Number.isNaN(base.getTime())) return null;
    base.setHours(
      Number(rowData.startHours),
      Number(rowData.startMinutes),
      0,
      0
    );
    return base;
  }

  return null;
};

const getRowEndDate = (rowData, startDate) => {
  const direct = parseDateValue(rowData?.endTime);
  if (direct) return direct;

  if (
    startDate &&
    rowData?.endTime?.endHours != null &&
    rowData?.endTime?.endMinutes != null
  ) {
    const end = new Date(startDate);
    end.setHours(
      Number(rowData.endTime.endHours),
      Number(rowData.endTime.endMinutes),
      0,
      0
    );
    return end;
  }

  return null;
};

export const UpdateRegularizationModal = ({
  rowData,
  teamProjects,
  teams,
  fetchProjectTasks,
  fetchRegulationRequests,
  onClose,
  onBusyChange,
}) => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.auth);
  const { activeCompany } = useSelector((state) => state.auth.profile);
  const { teams: userTeamsList } = useUserTeamsForCompany(
    profile?.id,
    activeCompany?.companyId
  );
  const [taskLists, setTaskLists] = useState([]);

  const [formData, setFormData] = useState(
    getInitialFormData(getDefaultTimezone())
  );
  const activeTeamId =
    teams.active?.activeTeamid ?? teams.active?.activeTeamId ?? null;
  const teamIdForManagers =
    formData.team?.value ?? rowData?.teamId ?? activeTeamId;
  const { memberDetailsUnfiltered } =
    useTeamMemberDetailsForSelect(teamIdForManagers);
  const availableManagers = useMemo(
    () => managersFromMemberDetails(memberDetailsUnfiltered),
    [memberDetailsUnfiltered]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const submitInFlightRef = useRef(false);
  const deleteInFlightRef = useRef(false);
  const setBusy = (busy) => {
    if (typeof onBusyChange === 'function') {
      onBusyChange(busy);
    }
  };
  const projectDropdownOptions = useMemo(() => {
    return getProjectDropdownOptions(teams);
  }, [teams?.projects]);
  const taskDropdownOptions = useMemo(() => {
    return getTaskDropdownOptions(taskLists);
  }, [taskLists]);
  const managerDropdownOptions = useMemo(() => {
    return getManagerDropdownOptions(availableManagers);
  }, [availableManagers]);
  const teamDropdownOptions = useMemo(
    () => getTeamDropdownOptions(userTeamsList),
    [userTeamsList]
  );
  const timezoneDropdownOptions = useTimezones();

  const todayMax = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  useEffect(() => {
    const project = teamProjects.find((p) => p.id === rowData.projectId);
    const startDate = getRowStartDate(rowData);
    const endDate = getRowEndDate(rowData, startDate);
    const selectedDate = startDate
      ? (() => {
          const d = new Date(startDate);
          d.setHours(0, 0, 0, 0);
          return d;
        })()
      : null;

    const selectedManager = managerDropdownOptions.find(
      (m) => m.value === rowData.assignee
    );

    const teamSelection = rowData.teamId
      ? (() => {
          const fromList = userTeamsList.find(
            (x) => x.teamId === rowData.teamId
          );
          return {
            value: rowData.teamId,
            label: fromList?.teamName || rowData.teamName || rowData.teamId,
          };
        })()
      : null;

    setFormData({
      team: teamSelection,
      projectId: project
        ? {
            ...project,
            label: project.data?.project_name,
            value: project.id,
          }
        : null,
      projectName: project?.data?.project_name || '',
      taskId: rowData.taskID || null,
      taskName: rowData.taskName || '',
      taskDetails: rowData.taskDetails || '',
      assignee: selectedManager || '',
      date: selectedDate,
      startTime: startDate ? dayjs(startDate) : null,
      endTime: endDate ? dayjs(endDate) : null,
      timezone: rowData.timezone
        ? {
            value: rowData.timezone,
            label: rowData.timezone,
          }
        : {
            value: getDefaultTimezone(),
            label: getDefaultTimezone(),
          },
    });
    if (!rowData.projectId) {
      setTaskLists([]);
      return;
    }

    fetchProjectTasks(rowData.projectId, (fetchedTasks) => {
      setTaskLists(fetchedTasks);
      const selectedTask = fetchedTasks.find((t) => t.id === rowData.taskID);
      setFormData((prev) => ({
        ...prev,
        taskId: selectedTask
          ? {
              ...selectedTask,
              label: selectedTask.Title,
              value: selectedTask.id,
            }
          : null,
      }));
    });
  }, [rowData, fetchProjectTasks, managerDropdownOptions, userTeamsList]);

  useEffect(() => {
    if (!teamIdForManagers || !availableManagers.length) return;
    setFormData((prev) => {
      const opts = getManagerDropdownOptions(availableManagers);
      const uid = prev.assignee?.userUID ?? prev.assignee?.value;
      if (uid && opts.some((o) => o.value === uid)) return prev;
      return { ...prev, assignee: '' };
    });
  }, [teamIdForManagers, availableManagers]);

  const handleTeamChange = (team) => {
    setFormData((prev) => ({
      ...prev,
      team,
      assignee: '',
    }));
  };
  const handleProjectChange = (project) => {
    setFormData((prev) => ({
      ...prev,
      projectId: project,
      projectName: project.label,
      taskId: null,
      taskName: '',
    }));
    fetchProjectTasks(project.id, (fetchedTasks) => setTaskLists(fetchedTasks));
  };
  const handleTaskChange = (task) => {
    setFormData((prev) => ({
      ...prev,
      taskId: task,
      taskName: task.label,
    }));
  };
  const handleAssigneeChange = (assignee) => {
    setFormData((prev) => ({
      ...prev,
      assignee: assignee,
    }));
  };
  const handleDateChange = (date) => {
    if (!date) {
      setFormData((prev) => ({ ...prev, date: null }));
      return;
    }
    if (!canUseRegularizationDateSelection(date)) return;
    setFormData((prev) => buildRegularizationFormDateChange(prev, date));
  };
  const handleTimezoneChange = (timezone) => {
    setFormData((prev) => ({
      ...prev,
      timezone: timezone,
    }));
  };
  const handleStartTimeChange = (startTime) => {
    setFormData((prev) => ({
      ...prev,
      startTime: startTime,
    }));
  };
  const handleEndTimeChange = (endTime) => {
    setFormData((prev) => ({
      ...prev,
      endTime: endTime,
    }));
  };
  const duration = useMemo(() => {
    const duration = calculateDuration(formData.startTime, formData.endTime);
    const durationText = duration.isValid
      ? `${String(duration.hours).padStart(2, '0')}h ${String(
          duration.minutes
        ).padStart(2, '0')}m`
      : '00h 00m';
    return { isValid: duration.isValid, durationText };
  }, [formData.startTime, formData.endTime]);

  const handleSubmit = async () => {
    if (submitInFlightRef.current || isSubmitting || isDeleting) return;
    submitInFlightRef.current = true;
    setIsSubmitting(true);
    setBusy(true);
    const isValid = validateForm(formData);
    if (!isValid) {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setBusy(false);
      return;
    }
    const timeLimitCheck = await check24HoursLimit(formData, teams, profile);
    if (!timeLimitCheck) {
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setBusy(false);
      return;
    }

    const duration = calculateDuration(formData.startTime, formData.endTime);
    const dateStr = dayjs(formData.date).format('YYYY-MM-DD');
    const { hours: startHours, minutes: startMinutes } = getHoursMinutes(
      formData.startTime
    );
    const { hours: endHours, minutes: endMinutes } = getHoursMinutes(
      formData.endTime
    );

    const updateData = {
      userId: profile.id,
      userName:
        (profile?.name && String(profile.name).trim()) || profile?.email || '',
      companyID: activeCompany.companyId,
      projectId: formData.projectId?.id ?? null,
      taskName: formData.taskName || '',
      taskID: formData.taskId?.id ?? null,
      taskDetails: formData.taskDetails || '',
      assignee: formData.assignee?.userUID,
      taskHours: duration.hours,
      taskMinutes: duration.minutes,
      status: 'Pending',
      typeLog: 'Manual',
      startTime: dateStr,
      requestDate: new Date(),
      endTime: { endHours, endMinutes },
      startHours,
      startMinutes,
      timezone: formData.timezone?.value || getDefaultTimezone(),
      teamId: formData.team?.value ?? null,
      teamName: formData.team?.label ?? null,
    };

    const result = await new Promise((resolve) => {
      dispatch({
        type: regularizationActionTypes.UPDATE_REQUEST,
        payload: {
          requestId: rowData.id,
          updateData,
          companyId: activeCompany.companyId,
          oldAssignee: rowData.assignee,
          newAssignee: formData.assignee?.userUID || rowData.assignee,
          onComplete: resolve,
        },
      });
    });
    if (result?.success) {
      onClose();
      setTimeout(() => {
        fetchRegulationRequests();
      }, 100);
    }
    submitInFlightRef.current = false;
    setIsSubmitting(false);
    setBusy(false);
  };
  const handleDelete = async () => {
    if (deleteInFlightRef.current || isSubmitting || isDeleting) return;
    deleteInFlightRef.current = true;
    setIsDeleting(true);
    setBusy(true);
    const result = await new Promise((resolve) => {
      dispatch({
        type: regularizationActionTypes.DELETE_REQUEST,
        payload: {
          requestId: rowData.id,
          companyId: activeCompany.companyId,
          assigneeId: rowData.assignee,
          userId: profile.id,
          onComplete: resolve,
        },
      });
    });
    if (result?.success) {
      onClose();
      setTimeout(() => {
        fetchRegulationRequests();
      }, 100);
    }
    deleteInFlightRef.current = false;
    setIsDeleting(false);
    setBusy(false);
  };

  return (
    <div className={styles.regularizationModal}>
      <Dropdown
        options={teamDropdownOptions}
        value={formData.team}
        label="Department"
        selectStyles={borderedSelectStyles}
        onChange={handleTeamChange}
        placeholder="Select Department"
      />
      <Dropdown
        options={projectDropdownOptions}
        value={formData.projectId}
        label="Project"
        selectStyles={borderedSelectStyles}
        onChange={handleProjectChange}
        placeholder="Select Project"
      />
      <Dropdown
        options={taskDropdownOptions}
        value={formData.taskId}
        label="Task"
        selectStyles={borderedSelectStyles}
        onChange={handleTaskChange}
        placeholder="Select Task"
      />
      <Input
        type="text"
        value={formData.taskDetails}
        label="Task Details"
        placeholder="Enter Task Details"
        className={styles.taskDetailsInput}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            taskDetails: e.target.value,
          }));
        }}
      />
      <Dropdown
        options={managerDropdownOptions}
        value={formData.assignee}
        label="Manager"
        selectStyles={borderedSelectStyles}
        onChange={handleAssigneeChange}
        placeholder="Select Manager"
      />
      <div className={styles.datePicker}>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          Date
        </Text>
        <DatePicker
          className={styles.datePickerInput}
          selected={formData.date}
          onChange={handleDateChange}
          placeholderText="Select date"
          dateFormat="yyyy-MM-dd"
          maxDate={todayMax}
          filterDate={isRegularizationDateSelectableInCalendar}
          popperClassName={styles.regularizationDatePickerPopper}
        />
      </div>
      <Dropdown
        options={timezoneDropdownOptions}
        value={formData.timezone}
        label="Timezone"
        selectStyles={borderedSelectStyles}
        onChange={handleTimezoneChange}
        placeholder="Select Timezone"
      />
      <div className={styles.timePicker}>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          Task Start Time
        </Text>
        <TimePicker
          use12Hours
          showSecond={false}
          value={formData.startTime}
          className={styles.timePickerInput}
          onChange={handleStartTimeChange}
          placeholder="Select Task Start Time"
          {...timePickerPopupProps}
        />
      </div>
      <div className={styles.timePicker}>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          Task End Time
        </Text>
        <TimePicker
          use12Hours
          showSecond={false}
          value={formData.endTime}
          className={styles.timePickerInput}
          onChange={handleEndTimeChange}
          placeholder="Select Task End Time"
          {...timePickerPopupProps}
        />
      </div>
      <div
        className={classNames(styles.duration, {
          [styles.duration_valid]: duration.isValid,
        })}
      >
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Normal}
        >
          Duration
        </Text>
        <Input value={duration.durationText} disabled />
      </div>
      <div className={styles.buttonContainer}>
        <Button
          text="Update"
          onClick={handleSubmit}
          variant={buttonVariants.rounded}
          loading={isSubmitting}
          disabled={isSubmitting || isDeleting}
        />
        <Button
          text="Delete"
          onClick={handleDelete}
          variant={buttonVariants.red}
          loading={isDeleting}
          disabled={isSubmitting || isDeleting}
        />
      </div>
    </div>
  );
};
